// hd-redesign-p1 — the offers rail.
//
// From the export's railStep/startRail/railDown. Carried verbatim:
//   · the step is 542px (a 520 card + the 22 gap)
//   · auto-advance every 4000ms, wrapping to 0 once within 4px of the end
//     — amended below: each wrap now fires when the step BEGAN at that end, so
//     a rail with less than one pitch of travel left still reaches both of them
//   · drag sets scroll-snap-type:none and restores 'x proximity' on release
//   · under reduced motion: no auto-advance, and scrollTo uses behavior 'auto'
//
// The drag suppresses the click that follows it, so dragging across a card does
// not also open that card — a plain pointerdown/up pair would.
//
// ── scrollLeft IS SIGNED, AND THE ARITHMETIC IS DONE IN LOGICAL SPACE ────────
// On an overflow container under dir="rtl", scrollLeft is 0 at the INLINE START
// (the right edge) and runs NEGATIVE toward the left, so the reachable range is
// [-(scrollWidth - clientWidth), 0] rather than [0, scrollWidth - clientWidth].
// Every line below that touched scrollLeft assumed the positive LTR range: the
// step added in the wrong direction, and the wrap compared a negative position
// against a positive `max` so it could never fire. On the Arabic storefront —
// this store's primary and default locale — the auto-advance walked off the end
// of the rail on its first tick and stayed there.
//
// The fix is to stop reasoning in physical space at all: `pos()` maps scrollLeft
// to a LOGICAL position that is always 0..max in both directions, all arithmetic
// happens there, and `seek()` maps it back. One expression carries the
// direction, which is the same discipline assets/hd-cart.css:250-258 states for
// the drawer's transform.
//
// THE DRAG IS LEFT ALONE, deliberately. `scrollLeft = l0 - d` is a PHYSICAL
// statement — drag the content right, the content moves right — and that is
// already correct in both directions. Rewriting it in logical space would
// invert the finger under Arabic.
//
// ── WHAT TOUCH CHANGED ──────────────────────────────────────────────────────
// The rail carried `touch-action: pan-y`, which hands the browser the vertical
// axis and claims the horizontal one for this script. On a phone that was the
// worst of both: the browser would not scroll the rail natively, and the custom
// drag it was reserved for is a pointer drag that the browser CANCELS the moment
// it decides the gesture is a page scroll — leaving `held` true and
// `.is-dragging` latched, with the auto-advance frozen for the rest of the
// visit, because nothing listened for pointercancel.
//
// Now the browser owns horizontal touch scrolling (`touch-action: pan-x pan-y`)
// and does it better than four lines of arithmetic — with inertia, with the
// snap points already declared, and with no threshold to tune. The custom drag
// stays for POINTING DEVICES, where there is no native drag to defer to and
// where touch-action does not apply at all. pointercancel is handled either way.
//
// THE AUTO-ADVANCE STILL RUNS AT EVERY WIDTH, which is the owner's call. Its
// only pause was mouseenter/mouseleave, and a finger produces neither, so a
// phone reader would have had the rail slide out from under them mid-sentence
// with no way to hold it. The hold is now gesture-agnostic: a pointer down or
// any scroll the shopper causes parks the timer for six seconds, and a scroll
// this script CAUSED is recognised by timestamp and ignored, or the rail would
// hold itself for ever after its first tick.
(function () {
  "use strict";

  // The export's number, and what the derivation below evaluates to at 1440.
  // Kept only as a fallback for a rail with fewer than two cards, where there is
  // no pitch to measure.
  var FALLBACK_STEP = 542;
  var HOLD_MS = 6000;

  function init(root) {
    var rail = root.querySelector("[data-rail]");
    if (!rail) return;

    var prev = root.querySelector("[data-rail-prev]");
    var next = root.querySelector("[data-rail-next]");
    var reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    var timer = null;
    var held = false;
    var holdTimer = null;
    var selfScrollUntil = 0;
    var fine = window.matchMedia("(pointer: fine)");

    // ONE PITCH, MEASURED, NOT TWO CONSTANTS. 542 was 520 + 22 restated in JS,
    // so the card's width and the distance it travels were two sources of truth
    // for one number — and below 961 the card is not 520 wide any more.
    function stepSize() {
      var cards = rail.querySelectorAll(".hd-p1-ocard");
      if (cards.length < 2) return FALLBACK_STEP;
      var a = cards[0].getBoundingClientRect();
      var b = cards[1].getBoundingClientRect();
      var d = Math.abs(b.left - a.left);
      return d > 1 ? d : FALLBACK_STEP;
    }

    function holdNow() {
      held = true;
      window.clearTimeout(holdTimer);
      holdTimer = window.setTimeout(function () { held = false; }, HOLD_MS);
    }

    // +1 under ltr, -1 under rtl. Read off the element rather than document.dir,
    // the shape assets/hd-before-after.js:68-76 uses.
    function sign() {
      return window.getComputedStyle(rail).direction === "rtl" ? -1 : 1;
    }

    function pos() {
      return sign() * rail.scrollLeft;
    }

    function seek(logical, smooth) {
      // claim the scroll events this is about to cause, so the hold below can
      // tell the shopper's finger from the interval's own work
      selfScrollUntil = Date.now() + 900;
      rail.scrollTo({
        left: sign() * logical,
        behavior: smooth && !reduced.matches ? "smooth" : "auto"
      });
    }

    // A SHORT LAST STEP LANDS ON THE END; ONLY A STEP THAT STARTED AT THE END
    // WRAPS. The export's two lines read `if (to > max - 4) to = 0` and
    // `if (to < 0) to = max`, and both ask the wrong question: they test where
    // the step would LAND, not where it began. A rail with less than one full
    // pitch of travel left therefore threw the whole remainder away and jumped
    // to the far end -- and when the travel is less than one pitch in TOTAL,
    // BOTH arrows became no-ops, each wrapping to the position it was already
    // standing on.
    //
    // That is not hypothetical. Removing the welcome-offer card leaves three
    // 520px cards in a 1440px rail: scrollWidth 1684, clientWidth 1440, so
    // max = 244 against a 542 pitch. Measured before this change, one click of
    // the advance arrow left scrollLeft at 40 (ltr) / -40 (rtl) at 1440 in both
    // locales, while 1024, 768 and 390 -- where max exceeds the pitch -- paged
    // correctly. It mattered at four cards too, where max was 786: the second
    // forward click wrapped from 582 rather than travelling the last 204px, so
    // the fourth card was never reachable from the arrows at 1440.
    //
    // THE LOOP IS KEPT, AND IT IS KEPT IN BOTH DIRECTIONS. A step that begins
    // at the end wraps to the start and a step that begins at the start wraps
    // to the end; any other short step lands on the boundary it was heading
    // for. Doing the forward half alone is what left `prev` dead at 1440: from
    // the end, one step back overshoots 0, and the old `to < 0` wrapped it
    // straight back to the end it had just come from.
    //
    // "AT THE START" IS THE PADDING, NOT ZERO. `scroll-snap-align: start` snaps
    // the first card's own edge, and the first card begins one rail padding in,
    // so a rail at rest reports scrollLeft 40 at 1440/1024 and 24 at 768/390 --
    // exactly .hd-p1-rail's padding at those widths. Testing `pos() < 4` would
    // therefore never once be true. It is read off the element rather than
    // restated here, and read as the INLINE padding so it is the right edge
    // under rtl without a second expression.
    // A RAIL WITH NOTHING TO SCROLL HAS NOTHING TO STEP OR DRAG. Below the
    // phone breakpoint the rail is a two-column GRID (assets/hd-p1.css,
    // RESPONSIVE): same markup, no overflow, arrows display:none. The interval
    // and the arrows still call step(), which would seek a scroll position the
    // element cannot take -- harmless but pointless -- and pointerdown would
    // still arm the drag, which is not harmless: a finger that moves 4px while
    // tapping a card sets `moved`, and the click that follows is swallowed. At
    // every width where the rail overflows -- 1440, 1024, 768 -- this is false
    // and nothing below changes.
    function inert() {
      return rail.scrollWidth <= rail.clientWidth + 1;
    }

    function step(dir) {
      if (inert()) return;
      var max = rail.scrollWidth - rail.clientWidth;
      var pad = parseFloat(window.getComputedStyle(rail).paddingInlineStart) || 0;
      var at = pos();
      var atStart = at <= Math.min(pad, max) + 4;
      var atEnd = at >= max - 4;
      var to = at + dir * stepSize();
      if (dir > 0) to = atEnd ? 0 : Math.min(to, max);
      else to = atStart ? max : Math.max(to, 0);
      seek(to, true);
    }

    function start() {
      if (reduced.matches) return;
      stop();
      timer = window.setInterval(function () {
        if (!held) step(1);
      }, 4000);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    if (prev)
      prev.addEventListener("click", function () {
        step(-1);
        start();
      });
    if (next)
      next.addEventListener("click", function () {
        step(1);
        start();
      });

    rail.addEventListener("mouseenter", function () {
      held = true;
    });
    rail.addEventListener("mouseleave", function () {
      held = false;
    });

    rail.addEventListener("pointerdown", function (e) {
      if (e.button !== 0) return;
      if (inert()) return;
      held = true;
      rail.classList.add("is-dragging");
      var x0 = e.clientX;
      var l0 = rail.scrollLeft;
      var moved = false;

      function move(ev) {
        var d = ev.clientX - x0;
        if (Math.abs(d) > 4) moved = true;
        rail.scrollLeft = l0 - d;
      }

      function up(ev) {
        rail.classList.remove("is-dragging");
        held = false;
        if (moved) {
          ev.preventDefault();
          ev.stopPropagation();
          // Swallow exactly the one click this drag would otherwise synthesise.
          rail.addEventListener(
            "click",
            function (ce) {
              ce.preventDefault();
              ce.stopPropagation();
            },
            { capture: true, once: true }
          );
        }
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up, true);
        window.removeEventListener("pointercancel", up, true);
      }

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up, true);
      window.addEventListener("pointercancel", up, true);
    });

    start();
    root.addEventListener("hd:teardown", stop);
  }

  function boot(scope) {
    Array.prototype.slice
      .call((scope || document).querySelectorAll("[data-rail-root]"))
      .forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
  });
  document.addEventListener("shopify:section:unload", function (e) {
    var root = e.target.querySelector("[data-rail-root]");
    if (root) root.dispatchEvent(new CustomEvent("hd:teardown"));
  });
})();
